import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';
import { colors } from '../brand-colors';

function IconographyStyle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Marker images (uploaded or generated)
  const [markerImages, setMarkerImages] = useState([]);
  const [primaryMarker, setPrimaryMarker] = useState(null);

  // Position, size, opacity
  const [position, setPosition] = useState('bottom-right');
  const [size, setSize] = useState('medium');
  const [opacity, setOpacity] = useState(85);

  // Upload
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Preview
  const [previewImage, setPreviewImage] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // AI generation (secondary option)
  const [showAiGenerate, setShowAiGenerate] = useState(false);
  const [generatingExamples, setGeneratingExamples] = useState(false);

  // Track if saved already
  const [hasSavedMarker, setHasSavedMarker] = useState(false);

  const previewTimeoutRef = useRef(null);

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-update preview when settings change
  useEffect(() => {
    if (primaryMarker) {
      // Debounce preview requests
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      previewTimeoutRef.current = setTimeout(() => {
        fetchPreview();
      }, 300);
    }
    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryMarker, position, size, opacity]);

  const initializePage = async () => {
    setLoading(true);
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }
    await fetchSavedMarker();
    setLoading(false);
  };

  const fetchSavedMarker = async () => {
    try {
      const response = await fetch('/api/brand/get-brand-marker', {
        method: 'GET',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.brand_marker) {
          const m = data.brand_marker;
          setPrimaryMarker(m.primary_image);
          setMarkerImages(m.all_images || []);
          setPosition(m.position || 'bottom-right');
          setSize(m.size || 'medium');
          setOpacity(m.opacity || 85);
          setHasSavedMarker(true);
        }
      }
    } catch (err) {
      console.log('Error fetching brand marker:', err);
    }
  };

  const fetchPreview = async () => {
    if (!primaryMarker) return;
    setLoadingPreview(true);
    try {
      const response = await fetch('/api/brand/preview-brand-marker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          marker_url: primaryMarker,
          position,
          size,
          opacity
        })
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewImage(`data:image/jpeg;base64,${data.preview}`);
      }
    } catch (err) {
      console.log('Error fetching preview:', err);
    }
    setLoadingPreview(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 4) {
      setUploadError('Maximum 4 marker files allowed');
      setTimeout(() => setUploadError(''), 4000);
      return;
    }
    setUploadError('');
    setUploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 4) {
      setUploadError('Maximum 4 marker files allowed');
      setTimeout(() => setUploadError(''), 4000);
      return;
    }
    setUploadError('');
    setUploadFiles(files);
  };

  const handleUploadMarkers = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append('icons', file));

      const response = await fetch('/api/brand/upload-brand-markers', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const newMarkers = data.markers || [];
        setMarkerImages(prev => [...prev, ...newMarkers]);
        setUploadFiles([]);

        // Auto-select first uploaded as primary if none selected
        if (!primaryMarker && newMarkers.length > 0) {
          setPrimaryMarker(newMarkers[0].url);
        }

        // Warn if no alpha
        const noAlpha = newMarkers.filter(m => !m.has_alpha);
        if (noAlpha.length > 0) {
          setUploadError(`Note: ${noAlpha.map(m => m.original_name).join(', ')} may not have a transparent background. For best results, use PNG with transparency.`);
          setTimeout(() => setUploadError(''), 8000);
        }
      } else {
        const errorData = await response.json();
        setUploadError(errorData.error || 'Failed to upload markers');
      }
    } catch (err) {
      setUploadError('Error uploading markers: ' + err.message);
    }

    setUploading(false);
  };

  const handleGenerateAiMarkers = async () => {
    setGeneratingExamples(true);
    setError('');

    try {
      // Use existing icon generation endpoint
      const response = await fetch('/api/brand/generate-icon-examples', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ num_images: 4 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.images) {
          const newMarkers = data.images.map(img => ({
            url: img.url || img.base64,
            has_alpha: true,
            original_name: 'AI Generated',
            base64: img.base64
          }));
          setMarkerImages(prev => [...prev, ...newMarkers]);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate marker concepts');
      }
    } catch (err) {
      setError('Error generating markers: ' + err.message);
    }

    setGeneratingExamples(false);
  };

  const handleSave = async () => {
    if (!primaryMarker) {
      setError('Please select a primary brand marker');
      setTimeout(() => setError(''), 4000);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/brand/save-brand-marker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          primary_image: primaryMarker,
          all_images: markerImages,
          position,
          size,
          opacity
        })
      });

      if (response.ok) {
        setHasSavedMarker(true);
        navigate('/brand-style');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save brand marker');
      }
    } catch (err) {
      setError('Error saving brand marker: ' + err.message);
    }

    setSaving(false);
  };

  const getImageSrc = (marker) => {
    if (marker.base64) return `data:image/png;base64,${marker.base64}`;
    if (marker.url) return marker.url;
    return marker;
  };

  const getMarkerUrl = (marker) => {
    if (typeof marker === 'string') return marker;
    return marker.url || marker;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h3 className="mb-0 text-center" style={{ fontFamily: "'Trajan Pro', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
                Brand Marker
              </h3>
            </Card.Header>
            <Card.Body className="p-4">
              <p className="text-center text-muted mb-4">
                Upload your brand mark — the icon, symbol, or graphic that appears on all your generated images.
                This makes your content instantly recognizable in any feed.
              </p>

              {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

              {hasSavedMarker && (
                <Alert variant="success" className="text-center mb-4">
                  Your brand marker is saved and will be applied to all newly generated images.
                </Alert>
              )}

              {/* Upload Section */}
              <Card className="mb-4" style={{ border: `2px dashed ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3 text-center">Upload Your Brand Marker</h5>
                  <p className="text-center text-muted mb-3">
                    <small>PNG with transparent background recommended — max 2.5 MB each, up to 4 files</small>
                  </p>
                  {uploadError && <Alert variant="warning" className="mb-3">{uploadError}</Alert>}
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => document.getElementById('marker-upload-input').click()}
                    style={{
                      border: `2px dashed ${colors.greyLight}`,
                      borderRadius: '8px',
                      padding: '30px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: '#fafafa',
                      transition: 'border-color 0.2s'
                    }}
                    onDragEnter={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = colors.goldMid; }}
                    onDragLeave={(e) => { e.currentTarget.style.borderColor = colors.greyLight; }}
                  >
                    <p className="mb-1" style={{ fontSize: '2rem' }}>+</p>
                    <p className="mb-0 text-muted">Click or drag & drop your brand marker images here</p>
                  </div>
                  <input
                    id="marker-upload-input"
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  {/* Preview selected files before upload */}
                  {uploadFiles.length > 0 && (
                    <div className="mt-3">
                      <Row>
                        {uploadFiles.map((file, idx) => (
                          <Col xs={3} key={idx} className="mb-2 text-center">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{ width: '100%', height: '80px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#f0f0f0' }}
                            />
                            <small className="text-muted d-block text-truncate">{file.name}</small>
                          </Col>
                        ))}
                      </Row>
                      <div className="text-center mt-2">
                        <Button
                          className="btn-brand-primary me-2"
                          onClick={handleUploadMarkers}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Uploading...
                            </>
                          ) : (
                            `Save ${uploadFiles.length} Marker${uploadFiles.length > 1 ? 's' : ''}`
                          )}
                        </Button>
                        <Button variant="outline-secondary" onClick={() => setUploadFiles([])}>
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* AI Generate (secondary) */}
              <div className="text-center mb-4">
                <Button
                  variant="link"
                  className="text-muted"
                  onClick={() => setShowAiGenerate(!showAiGenerate)}
                >
                  {showAiGenerate ? 'Hide AI generation' : "Don't have a brand mark? Generate concepts with AI"}
                </Button>
              </div>

              {showAiGenerate && (
                <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <Card.Body className="text-center">
                    <p className="text-muted mb-3">
                      Generate brand mark concepts based on your brand archetypes. These are AI interpretations
                      you can use as-is or as inspiration.
                    </p>
                    <Button
                      variant="outline-dark"
                      onClick={handleGenerateAiMarkers}
                      disabled={generatingExamples}
                    >
                      {generatingExamples ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating Concepts...
                        </>
                      ) : (
                        'Generate Brand Mark Concepts'
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              )}

              {/* Marker Gallery — select primary */}
              {markerImages.length > 0 && (
                <Card className="mb-4">
                  <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                    <h5 className="mb-0">Select Your Primary Brand Marker</h5>
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted mb-3">Click a marker to set it as your primary — this is the one that will appear on all generated images.</p>
                    <Row>
                      {markerImages.map((marker, idx) => {
                        const markerUrl = getMarkerUrl(marker);
                        const isPrimary = primaryMarker === markerUrl;
                        return (
                          <Col xs={6} md={3} key={idx} className="mb-3">
                            <Card
                              style={{
                                cursor: 'pointer',
                                border: isPrimary ? `3px solid ${colors.goldMid}` : '2px solid #ddd',
                                backgroundColor: isPrimary ? '#fdf8ed' : 'white',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => setPrimaryMarker(markerUrl)}
                            >
                              <Card.Body className="text-center p-3 position-relative">
                                {isPrimary && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '6px',
                                      right: '6px',
                                      backgroundColor: colors.goldMid,
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 'bold',
                                      fontSize: '14px'
                                    }}
                                  >
                                    &#10003;
                                  </div>
                                )}
                                <div style={{ backgroundColor: '#e8e8e8', borderRadius: '6px', padding: '10px', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    src={getImageSrc(marker)}
                                    alt={`Marker ${idx + 1}`}
                                    style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
                                  />
                                </div>
                                <small className={isPrimary ? 'fw-bold mt-2 d-block' : 'text-muted mt-2 d-block'} style={isPrimary ? { color: colors.goldDark } : {}}>
                                  {isPrimary ? 'Primary' : `Marker ${idx + 1}`}
                                </small>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Position, Size, Opacity + Live Preview */}
              {primaryMarker && (
                <Card className="mb-4">
                  <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                    <h5 className="mb-0">Placement & Preview</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {/* Controls */}
                      <Col md={5}>
                        {/* Position */}
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-bold">Position</Form.Label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxWidth: '200px' }}>
                            {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(pos => (
                              <Button
                                key={pos}
                                variant={position === pos ? 'dark' : 'outline-secondary'}
                                size="sm"
                                onClick={() => setPosition(pos)}
                                style={position === pos ? { backgroundColor: colors.greyDarkest, color: colors.goldLight } : {}}
                              >
                                {pos.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              </Button>
                            ))}
                          </div>
                        </Form.Group>

                        {/* Size */}
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-bold">Size</Form.Label>
                          <div className="d-flex gap-2">
                            {['small', 'medium', 'large'].map(s => (
                              <Button
                                key={s}
                                variant={size === s ? 'dark' : 'outline-secondary'}
                                size="sm"
                                onClick={() => setSize(s)}
                                style={size === s ? { backgroundColor: colors.greyDarkest, color: colors.goldLight } : {}}
                              >
                                {s.charAt(0).toUpperCase() + s.slice(1)} ({s === 'small' ? '10%' : s === 'medium' ? '15%' : '20%'})
                              </Button>
                            ))}
                          </div>
                        </Form.Group>

                        {/* Opacity */}
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Opacity: {opacity}%</Form.Label>
                          <Form.Range
                            min={30}
                            max={100}
                            value={opacity}
                            onChange={(e) => setOpacity(parseInt(e.target.value))}
                          />
                          <div className="d-flex justify-content-between">
                            <small className="text-muted">Subtle</small>
                            <small className="text-muted">Full</small>
                          </div>
                        </Form.Group>
                      </Col>

                      {/* Preview */}
                      <Col md={7}>
                        <div className="text-center">
                          <Form.Label className="fw-bold d-block mb-2">Live Preview</Form.Label>
                          {loadingPreview && !previewImage ? (
                            <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                              <Spinner animation="border" size="sm" />
                              <span className="ms-2">Generating preview...</span>
                            </div>
                          ) : previewImage ? (
                            <div style={{ position: 'relative' }}>
                              <img
                                src={previewImage}
                                alt="Brand marker preview"
                                style={{ width: '100%', borderRadius: '8px', border: '1px solid #ddd', opacity: loadingPreview ? 0.6 : 1, transition: 'opacity 0.2s' }}
                              />
                              {loadingPreview && (
                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                  <Spinner animation="border" size="sm" />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                              <span className="text-muted">Preview will appear here</span>
                            </div>
                          )}
                          <small className="text-muted mt-2 d-block">
                            Sample preview — your marker will appear at this position on all generated images
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Video Intro/Outro — Coming Soon */}
              <Card className="mt-4" style={{ opacity: 0.5, pointerEvents: 'none' }}>
                <Card.Header style={{
                  backgroundColor: colors.greyDarkest,
                  color: colors.goldLight,
                  fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                  letterSpacing: '0.1em',
                }}>
                  <strong>Video Intro & Outro</strong>
                  <span className="float-end" style={{ fontSize: '0.75rem', opacity: 0.7 }}>Coming Soon</span>
                </Card.Header>
                <Card.Body className="p-4">
                  <p className="text-muted mb-0">
                    Upload your branded video intro and outro clips. These will be automatically stitched onto every video
                    in your content atomization chain — ensuring consistent brand recognition across YouTube, Reels, Shorts, and TikTok.
                  </p>
                  <div className="d-flex gap-3 mt-3">
                    <div className="flex-fill p-3 text-center rounded" style={{ border: `2px dashed ${colors.greyLight}` }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>▶</div>
                      <small className="text-muted">Intro clip (3-5 sec)</small>
                    </div>
                    <div className="flex-fill p-3 text-center rounded" style={{ border: `2px dashed ${colors.greyLight}` }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>⏹</div>
                      <small className="text-muted">Outro clip (5-8 sec)</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Save */}
              <div className="text-center mt-4">
                {primaryMarker ? (
                  <Button
                    variant="primary"
                    size="lg"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      'Save & Continue →'
                    )}
                  </Button>
                ) : (
                  <Button variant="outline-secondary" onClick={() => navigate('/brand-style')}>
                    Skip for Now →
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default IconographyStyle;
